import { useCallback, useEffect, useMemo, useState } from "react";
import { Mail, Smartphone, AlertCircle } from "lucide-react";

import { SwivuList } from "@/modern/components/swivu-list";
import { Combobox, type TComboOption } from "@/modern/components/ui/combobox";
import {
  getNotifyPreferenceRecords,
  getNotifyPreferenceStore,
  type NotifyPreferenceRecord,
} from "@/modern/components/notification";
import { NOTIFY_PREFS_TYPES_TEXT } from "@/other/constants/OtherKeys";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";

import { SubcategoryDetailProps } from "../constants/settingsStructure";

const NotificationDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user } = useSwivuBryntumContext();
  const [notifyPreferences, setNotifyPreferences] = useState<
    NotifyPreferenceRecord[]
  >([]);

  const { subscribe } = useSwivuStore({
    storeId: STORE_KEYS.notifyPreferenceStore,
  });

  const currentUserId = user?.id ?? undefined;

  const loadNotifyPreferences = useCallback(() => {
    if (!project) return;
    setNotifyPreferences(getNotifyPreferenceRecords(project, currentUserId));
  }, [project, currentUserId]);

  useEffect(() => {
    loadNotifyPreferences();
    return subscribe(() => {
      loadNotifyPreferences();
    });
  }, [subscribe, loadNotifyPreferences]);

  const handleReorderItems = (
    movedItem: NotifyPreferenceRecord,
    beforeItem: NotifyPreferenceRecord | null,
  ) => {
    const notifyPreferenceStore = getNotifyPreferenceStore(project);
    if (!notifyPreferenceStore) return;
    // @ts-expect-error - store.move is a valid Bryntum Store method
    notifyPreferenceStore.move(movedItem, beforeItem);
  };

  const notificationMethodsOptions: TComboOption[] = useMemo(
    () => [
      {
        label: "Email",
        value: "email",
        icon: <Mail className="h-4 w-4 text-blue-500" />,
      },
      {
        label: "Workforce App",
        value: "workforce",
        icon: <Smartphone className="h-4 w-4 text-green-500" />,
      },
      {
        label: "Interrupt",
        value: "interrupt",
        icon: <AlertCircle className="h-4 w-4 text-orange-500" />,
      },
    ],
    [],
  );

  const getNotificationMethodsValue = (
    notifyPref: NotifyPreferenceRecord,
  ): string[] => {
    const values: string[] = [];
    if (notifyPref.isEmailNotify) values.push("email");
    if (notifyPref.isWorkforceNotify) values.push("workforce");
    if (notifyPref.interupt) values.push("interrupt");
    return values;
  };

  const handleNotificationMethodsChange = (
    notifyPref: NotifyPreferenceRecord,
    newValues: string[] | null,
  ) => {
    const values = Array.isArray(newValues) ? newValues : [];
    notifyPref.isEmailNotify = values.includes("email");
    notifyPref.isWorkforceNotify = values.includes("workforce");
    notifyPref.interupt = values.includes("interrupt");
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {subcategory.label}
        </h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <div className="max-w-4xl overflow-x-auto">
        <SwivuList.Root<NotifyPreferenceRecord>
          items={notifyPreferences}
          onReorder={handleReorderItems}
        >
          <div className="flex items-center gap-2 px-2.5 py-2 mb-2 bg-muted/30 rounded-md border border-border/40 min-w-[560px]">
            <div className="w-8 flex-shrink-0" />

            <div className="flex-shrink-0 w-52 min-w-[13rem]">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Notification Type
              </span>
            </div>

            <div className="flex-1 min-w-[320px] px-2">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Notification Methods
                </span>
                <span className="text-[10px] text-muted-foreground/60">
                  (How you receive alerts)
                </span>
              </div>
            </div>
          </div>

          <SwivuList.Content<NotifyPreferenceRecord>
            emptyMessage="No notification preferences found."
            className="min-w-max"
          >
            {(notifyPref: NotifyPreferenceRecord) => (
              <SwivuList.Item value={notifyPref.id}>
                <SwivuList.ItemHandle value={notifyPref.id} />

                <div className="flex-shrink-0 w-52 min-w-[13rem] px-2">
                  <span className="text-sm font-medium text-foreground">
                    {NOTIFY_PREFS_TYPES_TEXT[notifyPref.notifyType]}
                  </span>
                </div>

                <div className="flex-1 min-w-max px-2">
                  <Combobox
                    options={notificationMethodsOptions}
                    value={getNotificationMethodsValue(notifyPref)}
                    onValueChange={(value) =>
                      handleNotificationMethodsChange(
                        notifyPref,
                        value as string[],
                      )
                    }
                    placeholder="Select methods"
                    multiSelect={true}
                    showBadges={true}
                    enableRemoveButton={true}
                    size="sm"
                  />
                </div>
              </SwivuList.Item>
            )}
          </SwivuList.Content>
        </SwivuList.Root>
      </div>
    </div>
  );
};

export default NotificationDetail;
